import React from "react";
import { Shell } from "@/components/Shell";
import { Link } from "react-router-dom";

const EFFECTIVE = "February 1, 2026";

export default function Privacy() {
    return (
        <Shell>
            <section className="section-pad">
                <div className="max-w-3xl mx-auto" data-testid="privacy-page">
                    <div className="label-base">Legal</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">Privacy Policy</h1>
                    <p className="mt-4 text-sm text-brand-mute">Effective date: {EFFECTIVE}</p>

                    <div className="prose prose-neutral max-w-none mt-10 space-y-8 text-[15px] leading-relaxed text-brand-ink">
                        <p>This Privacy Policy explains how <strong>HealthFairX</strong> ("HealthFairX", "we", "us", or "our") collects, uses, and shares information when you use our website, marketplace, and related services (collectively, the "Services").</p>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">1. Information we collect</h2>
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong>Account information:</strong> name, email address, password (stored hashed), role (host company or vendor), and business profile details you provide.</li>
                                <li><strong>Event &amp; vendor data:</strong> event listings, vendor categories, service area, and application activity you submit to the platform.</li>
                                <li><strong>Mobile phone number &amp; SMS opt-in:</strong> if you choose to receive text alerts, we collect your mobile number and the timestamp, IP address, user agent, and source URL of your opt-in for compliance recordkeeping.</li>
                                <li><strong>Payment information:</strong> processed by our payment processor (Stripe). HealthFairX does not store full card numbers.</li>
                                <li><strong>Usage data:</strong> standard server logs, device and browser metadata, and cookies necessary for authentication and basic analytics.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">2. How we use your information</h2>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>To operate the marketplace and match hosts with relevant vendors.</li>
                                <li>To send transactional emails and (with your opt-in) transactional SMS such as event reminders and vendor application status updates.</li>
                                <li>To process payments for booth fees.</li>
                                <li>To enforce our Terms of Service, prevent fraud, and comply with legal obligations.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">3. SMS messaging program</h2>
                            <p>HealthFairX operates an opt-in SMS program for event reminders and vendor application status updates. Key terms:</p>
                            <ul className="list-disc pl-6 space-y-2 mt-3">
                                <li>You must explicitly opt in via our web form at <Link to="/sms-opt-in" className="underline">/sms-opt-in</Link>. We do not pre-check the consent checkbox.</li>
                                <li>Message frequency is <strong>event-driven</strong> — most users receive a few messages per month.</li>
                                <li>Message and data rates may apply.</li>
                                <li>You can text <strong>STOP</strong> to any HealthFairX number at any time to unsubscribe, or <strong>HELP</strong> for support.</li>
                                <li>We will <strong>never share or sell your mobile phone number or SMS opt-in data</strong> with third parties for marketing purposes. Mobile information is not shared with affiliates for promotional or marketing reasons.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">4. How we share information</h2>
                            <p>We share information only as needed to operate the Services, including with:</p>
                            <ul className="list-disc pl-6 space-y-2 mt-3">
                                <li>Service providers acting on our behalf (hosting, email delivery, SMS delivery via Twilio, payments via Stripe, address geocoding).</li>
                                <li>Other platform users, only where necessary for the marketplace to function (e.g., your business name and category are visible to hosts who match with you).</li>
                                <li>Authorities, if required by law or to protect rights, safety, or property.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">5. Data retention</h2>
                            <p>We retain account information for as long as your account is active. Opt-in records for SMS are retained for the life of your subscription plus a reasonable period afterward for compliance and dispute resolution.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">6. Your choices</h2>
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong>Email:</strong> you can unsubscribe from non-transactional emails via the link in each message.</li>
                                <li><strong>SMS:</strong> text STOP to unsubscribe at any time.</li>
                                <li><strong>Account:</strong> contact us to deactivate your account or request a copy of your data.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">7. Security</h2>
                            <p>We use industry-standard practices including TLS in transit and hashed passwords at rest. No system is perfectly secure; please keep your password confidential.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">8. Children</h2>
                            <p>The Services are not directed to children under 13, and we do not knowingly collect personal information from them.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">9. Changes</h2>
                            <p>We may update this Privacy Policy. We will revise the effective date above and, for material changes, notify users via email or in-product notice.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">10. Contact</h2>
                            <p>Questions about this policy or your data: <a href="mailto:support@healthfairx.com" className="underline" data-testid="privacy-contact-email">support@healthfairx.com</a>.<br />HealthFairX — District of Columbia, USA.</p>
                        </div>
                    </div>
                </div>
            </section>
        </Shell>
    );
}
